<!-- `data-section-key` is how the vehicle form's ad wizard tells two copies of
     the SAME field apart: Index.hr reuses Njuškalo's field uuids verbatim, so
     `njuskalo_model` is in the DOM twice and a plain lookup by uuid hit
     whichever rendered first — the wizard could send the dealer to the other
     portal's copy of the field it was naming (BRIEFD-7189). -->
<div [hidden]="isHidden" [ngClass]="{'py-3': sectionItem.type !== 'group'}"
     [attr.data-section-key]="sectionItem.key || null">
  <!-- Standard ribbon header (non-collapsible sections) -->
  <div *ngIf="sectionItem.title && sectionItem.type !== 'group' && !hideHeader && !sectionItem.collapsible" class="h-20">
    <div class="bg-primaryDark py-4 px-8 absolute ribbon" style="width: calc(100% + 1rem); left: -0.5rem">
      <h4 class="text-white text-sm font-bold">
        {{ sectionItem.title }}
        <app-tooltip [content]="sectionItem.description"></app-tooltip>
        <button *ngIf="hasSectionSettings"
                type="button"
                class="ml-2 text-white hover:opacity-70"
                (click)="openSectionSettings($event)"
                title="Nastavitve" i18n-title
                aria-label="Nastavitve" i18n-aria-label>
          <i class="fas fa-cog"></i>
        </button>
      </h4>
    </div>
  </div>

  <!-- Collapsible section header — styled as a header (not a field) so it
       reads as a heading with a chevron, matching the standard ribbon
       style of non-collapsible sections but with an expand affordance.
       NOTE: not gated on `hideHeader` — when a parent passes
       `hideHeader=true` (e.g. the v2 vehicle form page does, to drop the
       big ribbon at the top of each tab), we still want per-integration
       collapsible subsections (Njuškalo / Bolha / ...) to show their
       toggleable headers. Without that header the accordion has no UI. -->
  <div *ngIf="sectionItem.title && sectionItem.type !== 'group' && sectionItem.collapsible"
       class="mx-0 md:mx-10 mb-2 cursor-pointer select-none flex items-center justify-between border-b border-primaryDark py-2 hover:opacity-80 transition-opacity"
       (click)="toggleSection()"
       (keydown.enter)="toggleSection()"
       (keydown.space)="toggleSection(); $event.preventDefault()"
       role="button"
       tabindex="0"
       [attr.aria-expanded]="isExpanded">
    <h4 class="text-xs font-bold uppercase tracking-wider text-primaryDark">
      {{ sectionItem.title }}
    </h4>
    <div class="flex items-center gap-3">
      <!-- Advertising portal sections get a link to their own settings popup
           (BRIEFD-7086). stopPropagation so it doesn't toggle the section. -->
      <button *ngIf="hasSectionSettings"
              type="button"
              class="flex items-center gap-1 text-xs font-semibold text-primaryDark hover:opacity-70 whitespace-nowrap"
              (click)="openSectionSettings($event)">
        <i class="fas fa-cog"></i>
        <span class="normal-case tracking-normal" i18n>Nastavitve portala</span>
      </button>
      <i class="fas text-xs text-primaryDark"
         [ngClass]="isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </div>
  </div>

  <!-- The legacy form hides section ribbons (hideHeader defaults to true), so a
       non-collapsible section with settings would have nowhere to put its link.
       Give it a compact one of its own (BRIEFD-7086). -->
  <div *ngIf="hasSectionSettings && !sectionItem.collapsible && hideHeader"
       class="mx-0 md:mx-10 mb-2 flex justify-end">
    <button type="button"
            class="text-xs text-primaryDark hover:opacity-70"
            (click)="openSectionSettings($event)">
      <i class="fas fa-cog mr-1"></i><span i18n>Nastavitve opisa podjetja</span>
    </button>
  </div>

  <div *ngIf="!sectionItem.collapsible || isExpanded"
       [ngClass]="{'grid-group md:px-10': sectionItem.type === 'group'}">

    <!-- BRIEFD-7341: a collapsible section's description reads as intro copy
         directly under its header rather than a tooltip — dealers were missing
         it. Only the Quick Entry section sets one today. -->
    <p *ngIf="sectionItem.collapsible && sectionItem.description"
       class="mx-0 md:mx-10 mb-3 text-xs text-gray-600">{{ sectionItem.description }}</p>

    <!-- trackBy index: the advertising tab's section is replaced wholesale by
         every `forms/preview` response, so its items are fresh objects each
         time. Without a trackBy the default identity check rebuilt the entire
         subtree — inputs, flag tabs, portal logos — on each refresh, which is
         the flicker and the late-arriving flags/logos (BRIEFD-7404). Items
         never reorder within a section, so position is a stable identity. -->
    <ng-container *ngFor="let item of sectionItem.items; let i = index; trackBy: trackByIndex">

      <ng-container [ngSwitch]="item.type">

        <ng-container *ngSwitchCase="'field'">
          <ng-container>
            <!-- No "Fotografije vozila" sub-heading here: this block is only
                 ever the first item of the section already titled "Fotografije",
                 so it restated the card's own title one line below it. The
                 "CarPeak odstranitev ozadja vozila" block below keeps its heading —
                 that one genuinely separates a second block. -->
            @if (form.fields[asFieldItem(item).fieldUuid].config.typeId === FieldTypeEnum.BRIEFD_VEHICLE_EXTERNAL_IMAGE_BACKGROUND_REMOVAL) {
              <div class="mx-0 md:mx-10 mt-8 mb-4">
                <hr class="mb-4 border-primaryDark border-2">
                <h3 class="text-base font-semibold text-primaryDark mb-2" i18n="@@carpeak.bg-removal.heading">CarPeak odstranitev ozadja vozila</h3>
                @if (form.fields[asFieldItem(item).fieldUuid].config.description) {
                  <p class="text-sm text-gray-600 mb-4 text-orange" [innerHTML]="form.fields[asFieldItem(item).fieldUuid].config.description"></p>
                }
              </div>
            }
            @if (form.fields[asFieldItem(item).fieldUuid].config.typeId === FieldTypeEnum.BRIEFD_VEHICLE_PURCHASE_PHOTOGRAPHS) {
              <div class="mx-0 md:mx-10 mb-4">
                <h3 class="text-xs font-semibold text-primaryDark mb-2" i18n>Slike ob nabavi vozila</h3>
              </div>
            }
            @if (isCostsCard(item) && form.fields[asFieldItem(item).fieldUuid].config.isVisible) {
              <app-v2-costs-card class="block mx-0 md:mx-10"
                                 [expenseRepeater]="form.fields[asFieldItem(item).fieldUuid]"
                                 [control]="costsControl(item)"></app-v2-costs-card>
            }
            <div
                    *ngIf="form.fields[asFieldItem(item).fieldUuid].config.isVisible && !isCostsCard(item)"
                    [ngClass]="{'mb-3': i < sectionItem.items.length - 1, 'flex-1': sectionItem.type === 'group', 'mx-0 md:mx-10': sectionItem.type !== 'group' && form.fields[asFieldItem(item).fieldUuid].type !== 'segmentation', 'flex flex-col-reverse': form.fields[asFieldItem(item).fieldUuid].type==='boolean'}">
              <app-form-input [formGroup]="formGroup" [field]="form.fields[asFieldItem(item).fieldUuid]"
                              [half]="form.fields[asFieldItem(item).fieldUuid].type !== 'boolean'"
                              [showBooleanDescription]="showBooleanDescription"
                              *ngIf="form.fields[asFieldItem(item).fieldUuid].type !== 'currency'; else currencyDiv"
                              (keydown.enter)="focusNextField($event)">
              </app-form-input>
              <ng-template #currencyDiv>
                <app-inline-field-edit [cancelEditOnSave]="true" textSize="sm"
                                       [updateField]="false"
                                       [formGroup]="formGroup"
                                       [field]="form.fields[asFieldItem(item).fieldUuid]"
                                       (update)="fieldsUpdated($event)"
                                       [isEditable]="!disabled"
                                       (keydown.enter)="focusNextField($event)"></app-inline-field-edit>
              </ng-template>
            </div>
          </ng-container>
        </ng-container>

        <ng-container *ngSwitchDefault>
          <div [ngClass]="{'mb-3': asSectionItem(item).items.length % 2 === 1}">

            <app-form-layout [sectionItem]="asSectionItem(item)"
                             [formGroup]="formGroup"
                             [disabled]="disabled"
                             [hidden]="isHidden"
                             [hideHeader]="hideHeader"
                             [showBooleanDescription]="showBooleanDescription"
                             [vehicleUuid]="vehicleUuid"
                             [costsCard]="costsCard"
                             (settingsSaved)="settingsSaved.emit()"
                             [form]="form"></app-form-layout>
          </div>
        </ng-container>
      </ng-container>
    </ng-container>
  </div>
</div>
